import type { Breadcrumb, Event } from '@sentry/nextjs';

const SEARCH_PATHS = new Set(['/v2/dashboard', '/v2/dashboard/']);
const SPAN_URL_KEYS = ['url', 'http.url', 'url.full'] as const;

export function scrubSearchUrlsInText(text: string): string {
  return text.replace(/(\/v2\/dashboard\/?)[?#][^\s"'<>]*/g, '$1');
}

export function isSearchPageUrl(url: string): boolean {
  try {
    return SEARCH_PATHS.has(new URL(url, 'http://localhost').pathname);
  } catch {
    return false;
  }
}

export function scrubSearchUrl(url: string): string {
  try {
    const parsedUrl = new URL(url, 'http://localhost');

    if (!SEARCH_PATHS.has(parsedUrl.pathname)) {
      return url;
    }

    const delimiterIndex = url.search(/[?#]/);
    return delimiterIndex === -1 ? url : url.slice(0, delimiterIndex);
  } catch {
    return url;
  }
}

export function scrubSearchBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (breadcrumb.data == null && breadcrumb.message == null) {
    return breadcrumb;
  }

  const data = breadcrumb.data == null ? undefined : { ...breadcrumb.data };

  if (data != null) {
    ['url', 'from', 'to'].forEach((key) => {
      if (typeof data[key] === 'string') {
        data[key] = scrubSearchUrl(data[key]);
      }
    });
  }

  return {
    ...breadcrumb,
    ...(data == null ? {} : { data }),
    ...(breadcrumb.message == null ? {} : { message: scrubSearchUrlsInText(breadcrumb.message) }),
  };
}

export function scrubSearchEvent<T extends Event>(event: T): T {
  const request = event.request == null ? undefined : { ...event.request };
  const requestIsSearch = request?.url != null && isSearchPageUrl(request.url);

  if (request != null) {
    if (typeof request.url === 'string') {
      request.url = scrubSearchUrl(request.url);
      if (requestIsSearch) {
        delete request.query_string;
      }
    }

    if (request.headers != null) {
      request.headers = Object.fromEntries(
        Object.entries(request.headers).map(([key, value]) => [key, scrubSearchUrlsInText(value)]),
      );
    }
  }

  const logentry =
    event.logentry == null
      ? undefined
      : {
          ...event.logentry,
          ...(event.logentry.message == null
            ? {}
            : { message: scrubSearchUrlsInText(event.logentry.message) }),
        };
  const exception =
    event.exception == null
      ? undefined
      : {
          ...event.exception,
          values: event.exception.values?.map((value) => ({
            ...value,
            ...(value.value == null ? {} : { value: scrubSearchUrlsInText(value.value) }),
          })),
        };

  const spans = event.spans?.map((span) => {
    const data = { ...span.data };
    const searchUrl = [span.description, ...SPAN_URL_KEYS.map((key) => data[key])].find(
      (value): value is string => typeof value === 'string' && isSearchPageUrl(value),
    );

    if (searchUrl == null) {
      return span;
    }

    SPAN_URL_KEYS.forEach((key) => {
      if (typeof data[key] === 'string') {
        data[key] = scrubSearchUrl(data[key]);
      }
    });
    delete data['http.query'];
    delete data['http.fragment'];

    return {
      ...span,
      ...(span.description == null ? {} : { description: scrubSearchUrl(span.description) }),
      data,
    };
  });

  return {
    ...event,
    ...(event.message == null ? {} : { message: scrubSearchUrlsInText(event.message) }),
    ...(logentry == null ? {} : { logentry }),
    ...(exception == null ? {} : { exception }),
    ...(event.transaction == null ? {} : { transaction: scrubSearchUrl(event.transaction) }),
    ...(request == null ? {} : { request }),
    ...(spans == null ? {} : { spans }),
  } as T;
}
